import React, {useState, useRef, useEffect} from 'react';
import {Field, Form, Formik} from 'formik';
import {Trans} from 'react-i18next';
import {history} from '../../../../utils/redux/store';
import RadioButton from '../../../../components/formik/RadioButton';
import _get from 'lodash/get';
import ImageField from '../../../../components/formik/ImageField';
import {toast} from 'react-toastify';
import Header from '../../components/header';
import ButtonWithRestoreGroup from '../../../../components/formik/ButtonWithRestoreGroup';
import {useSelector} from 'react-redux';
import {
  handleImage,
  getTemplateDefaultConfig,
  getTemplate,
  updateTemplate,
  getBrandId,
} from '../../../../utils/skincareUtils';
import smbUtils from '../../../../utils/smbUtils';
import Loader from '../../../../components/spinner/Loader';
import useWindowWidth from '../../../../components/hook/useWindowWidth';
import classnames from 'classnames';
import _isEmpty from 'lodash/isEmpty';
import PublishToOnlineModal from '../components/PublishToOnlineModal';
import InfoDialog from '../../../../components/dialogs/InfoDialog';
import CreateAccountForm from './component/createaccountform';
import {solutionUtils} from '../../../../utils/solutionUtils';
import {privilegeUtils} from '../../../../utils/privilegeUtils';
import useSkincare from '../../../../components/hook/useSkincare';
import ColorThemeSettings from './component/ColorThemeSettings';
import ProcessingInProgressModal from '../components/ProcessingInProgressModal';
import { isFunctionBusyError } from '../../../../utils/errorUtils';

export default function CommonSettings() {
  const width = useWindowWidth();
  const auth = useSelector(state => state.components.auth.accountSummary);
  const {token, customerId, brandInfo} = smbUtils.customerInfo(auth);
  const [isLoaded, setIsLoaded] = useState(true);
  const [publishStatus, setPublishStatus] = useState(false);
  const [isShowPublishModal, setIsShowPublishModal] = useState(false);
  const [isShowProcessingModal, setIsShowProcessingModal] = useState(false);
  const [initValue, setInitValue] = useState({});
  const [templateId, setTemplateId] = useState('');
  const {isVersionAbove2AndHd} = useSkincare(auth);

  const handleRestore = () => {
    getTemplateDefaultConfig('SmbCommonSetting', 'Light', 2, customerId).then(
      r => {
        if (r.pageConfig) {
          updateTemplate(
            token,
            customerId,
            templateId,
            'CommonSetting',
            r.templateMetadata,
            r.fileId,
            r.pageConfig
          ).then(() => {
            setPublishStatus(!publishStatus);
            setInitValue(_get(r.pageConfig, 'page[0]'));
            toast.success(
              <Trans i18nKey="c.f.simple.button.group.success">success</Trans>
            );
            window.location.reload();
          });
        }
      }
    );
  };

  useEffect(() => {
    if (_get(brandInfo, 'templateVersion') === 1) {
      history.push('/skincare-pro/ui-configuration/landing-page');
    }
    getBrandId(token, customerId)
      .then(res => {
        if (res && res.commonSettingRvId) {
          getTemplate(
            token,
            customerId,
            _get(res, 'commonSettingRvId', ''),
            'CommonSetting'
          ).then(res => {
            if (res && res.configJson) {
              setInitValue(_get(JSON.parse(res.configJson), 'page[0]'));
            }
            setTemplateId(_get(res, 'templateId', ''));
          });
        }
      })
      .then(() => setIsLoaded(false));
  }, []);

  const getInitValue = values => {
    if (_isEmpty(values)) return {};
    return {
      ...values,
      score: {
        scoreMeasurement: _get(values, 'score.scoreMeasurement', 'refined'),
        scoreFormat: _get(values, 'score.scoreFormat', 'percentile'),
      },
      header: {
        ...values.header,
        logo: {
          ...values.header.logo,
          imageFile:
            values.header.logo.imageFile || values.header.logo.imageUrl,
        },
        displayName: _get(values, 'header.displayName', 'showAll'),
      },
      theme: {
        color: {
          actionButton: _get(values, 'theme.color.actionButton', '#000000'),
          primary: _get(values, 'theme.color.primary', '#028CF0'),
          secondary: _get(values, 'theme.color.secondary', '#D4F0F9'),
          secondary2: _get(values, 'theme.color.secondary2', '#D0D5EC'),
        },
      },
      isHDAnalysis: _get(values, 'isHDAnalysis', isVersionAbove2AndHd),
    };
  };
  const formRef = useRef();
  const formikConfig = {
    enableReinitialize: true,
    initialValues: getInitValue(initValue),
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true);
      const header = await handleImage(
        _get(values, 'header.logo.imageFile') ||
          _get(values, 'header.logo.imageUrl'),
        token,
        customerId
      );
      const data = {
        page: [
          {
            templateType: 'skincare',
            pageType: 'getSkincareResult',
            ...values,
            header: {
              logo: {
                image: './assets/images/header_logo.png',
                imageUrl: header,
              },
              displayName: _get(values, 'header.displayName', 'showAll'),
            },
            isHDAnalysis: _get(values, 'isHDAnalysis', isVersionAbove2AndHd),
          },
        ],
      };
      await updateTemplate(
        token,
        customerId,
        templateId,
        'CommonSetting',
        {
          files: [
            {
              file_url: header,
              path: './assets/images/header_logo.png',
            },
          ],
        },
        '',
        data
      )
        .then(r => setIsShowPublishModal(true))
        .catch(e => {
          if (isFunctionBusyError(e)) {
            setIsShowProcessingModal(true);
            setSubmitting(false);
            return;
          }

          toast.error(
            <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
          );
          setSubmitting(false);
        });
    },
  };

  if (
    !(
      privilegeUtils.canUiConfig(auth) ||
      privilegeUtils.isTechnicalSupport(auth)
    )
  ) {
    history.push('/');
  }

  return isLoaded ? (
    <div>
      <Header />
      <Loader />
    </div>
  ) : (
    <Formik {...formikConfig} ref={formRef}>
      {formikProps => (
        <Form className="skincare-configuration-form">
          <Header publishStatus={publishStatus} dirty={formikProps.dirty} />
          <div className="skincare-configuration-layout">
            <div>
              <div className="kt-portlet skincare-configuration-layout__left-portlet">
                <div className="kt-portlet__body skincare-configuration-layout__left-portlet__body">
                  <div>
                    <h5>
                      <Trans i18nKey="p.s.uiconfiguration.getskinresult.header">
                        Header
                      </Trans>
                      <div
                        className="kt-margin-t-10"
                        style={{
                          backgroundColor: '#e1e1ef',
                          height: '1px',
                        }}
                      />
                    </h5>
                    <div className="form-group row">
                      <label className="col-3 col-form-label text-right">
                        <Trans i18nKey="p.s.uiconfiguration.common.header.logo">
                          Header Logo
                        </Trans>
                      </label>
                      <div className="col-4 col-lg-4 col-md-4">
                        <Field
                          name="header.logo.imageFile"
                          imageUrl={_get(
                            formikProps.values,
                            'header.logo.imageUrl'
                          )}
                          scaleImage={true}
                          clearFormFields={[
                            'header.logo.imageFile',
                            'header.logo.imageUrl',
                          ]}
                          component={ImageField}
                          width={760}
                          height={148}
                          maxWidth={380}
                          maxHeight={74}
                        />
                      </div>
                    </div>
                    <div
                      className="form-group row"
                      style={{alignItems: 'center'}}
                    >
                      <label className="col-3 col-form-label text-right">
                        <Trans i18nKey="p.s.uiconfiguration.common.header.display.name">
                          Displayed Name Below Logo
                        </Trans>
                      </label>
                      <div
                        className="d-flex col-9 row"
                        style={{
                          display: width >= 1366 ? 'flex' : 'block',
                          alignItems: 'center',
                        }}
                      >
                        <div className="col-12">
                          <Field
                            name={'header.displayName'}
                            label={
                              <Trans i18nKey="p.s.uiconfiguration.common.show.both">
                                Show Both
                              </Trans>
                            }
                            value={'showAll'}
                            component={RadioButton}
                            style={{
                              marginRight: width > 1024 ? '20px' : '10px',
                              marginTop: '10px',
                            }}
                          />
                          <Field
                            name={'header.displayName'}
                            label={
                              <Trans i18nKey="p.s.uiconfiguration.common.show.account.business.name">
                                Show Account Business Name Only
                              </Trans>
                            }
                            value={'useAccountName'}
                            component={RadioButton}
                            style={{
                              marginRight: width > 1024 ? '20px' : '10px',
                              marginTop: '10px',
                            }}
                          />
                          <Field
                            name={'header.displayName'}
                            label={
                              <Trans i18nKey="p.s.uiconfiguration.common.show.store.name">
                                Show Store Name Only
                              </Trans>
                            }
                            value={'useStoreName'}
                            component={RadioButton}
                            style={{marginTop: '10px'}}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <ColorThemeSettings />
                  {solutionUtils.isSkinAnalysis(auth) && (
                    <div>
                      <h5>
                        <Trans i18nKey="p.s.uiconfiguration.common.score.mode">
                          Score Modes
                        </Trans>
                        <div
                          className="kt-margin-t-10"
                          style={{
                            backgroundColor: '#e1e1ef',
                            height: '1px',
                          }}
                        />
                      </h5>
                      <p className="kt-margin-t-10">
                        <Trans i18nKey="p.s.uiconfiguration.common.score.mode.desc">
                          Select the score modes that best align with your
                          business needs. Changes to score modes will only apply
                          to newly created skin reports; existing reports will
                          remain unaffected. If you make any changes, be sure to
                          review the Recommendation Rule Settings to keep
                          everything up-to-date.
                        </Trans>
                      </p>
                      <div className="row" style={{alignItems: 'center'}}>
                        <label className="col-3 col-form-label text-right">
                          <InfoDialog
                            trigger={'click'}
                            placement="right"
                            description={
                              <Trans i18nKey="p.s.uiconfiguration.common.score.measurement.desc">
                                <li>
                                  Original Score: The original skin concern
                                  rating from Skincare Pro AI algorithm.
                                </li>
                                <li>
                                  Refined Score: A mathematically adjusted score
                                  based on the Original Score.
                                </li>
                              </Trans>
                            }
                          >
                            <label className="cursor-help">
                              <Trans i18nKey="p.s.uiconfiguration.common.score.measurement">
                                Score Measurement
                              </Trans>{' '}
                              <i className="fa fa-question-circle" />
                            </label>
                          </InfoDialog>
                        </label>
                        <div
                          className="col-9 row"
                          style={{display: width >= 1366 ? 'flex' : 'block'}}
                        >
                          <div
                            className={classnames({
                              'col-6': width >= 1366,
                              'col-12': width < 1366,
                            })}
                          >
                            <Field
                              name={'score.scoreMeasurement'}
                              label={
                                <Trans i18nKey="p.s.uiconfiguration.common.initial.score">
                                  Original Score
                                </Trans>
                              }
                              value={'initial'}
                              component={RadioButton}
                              style={{marginRight: '20px'}}
                            />
                            <Field
                              name={'score.scoreMeasurement'}
                              label={
                                <Trans i18nKey="p.s.uiconfiguration.common.refined.score">
                                  Refined Score
                                </Trans>
                              }
                              value={'refined'}
                              component={RadioButton}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row" style={{marginTop: '-10px'}}>
                        <div className="col-3" />
                        <div className="col-9">
                          <label style={{color: '#a9acb8'}}>
                            <Trans i18nKey="p.s.uiconfiguration.common.score.measurement.tip">
                              * Tell me more about{' '}
                              <a
                                onClick={() => {
                                  window.open(
                                    'https://plugins-media.makeupar.com/pfweb/general/Skincare_Pro_Skin_Concerns_and_Scores_en.pdf'
                                  );
                                }}
                                href="javascript:;"
                                className="kt-link text-primary cursor-pointer"
                                style={{whiteSpace: 'nowrap'}}
                              >
                                skin concerns and score measurements{' '}
                                <i className="fa fa-external-link-alt" />
                              </a>
                            </Trans>
                          </label>
                        </div>
                      </div>
                      <div
                        className="row kt-margin-t-20"
                        style={{alignItems: 'center'}}
                      >
                        <label className="col-3 col-form-label text-right">
                          <Trans i18nKey="p.s.uiconfiguration.common.score.format">
                            Score Format
                          </Trans>
                        </label>
                        <div
                          className="col-9 row"
                          style={{display: width >= 1366 ? 'flex' : 'block'}}
                        >
                          <div
                            className={classnames({
                              'col-8': width >= 1366,
                              'col-12': width < 1366,
                            })}
                          >
                            <div className="row">
                              <div className="col-12 col-md-6 col-sm-6">
                                <Field
                                  name={'score.scoreFormat'}
                                  label={
                                    <>
                                      <Trans i18nKey="p.s.uiconfiguration.common.score.format.percentile">
                                        Percentile Rating
                                      </Trans>{' '}
                                      (1~100)
                                    </>
                                  }
                                  value={'percentile'}
                                  component={RadioButton}
                                  style={{marginRight: '20px'}}
                                />
                                <img
                                  src={require('../../../../assets/images/skincare/skincarepro_score_percentile.png')}
                                  alt={''}
                                  style={{width: 'inherit'}}
                                />
                              </div>
                              <div className="col-12 col-md-6 col-sm-6">
                                <Field
                                  name={'score.scoreFormat'}
                                  label={
                                    <>
                                      <Trans i18nKey="p.s.uiconfiguration.common.score.format.decile">
                                        Decile Rating
                                      </Trans>{' '}
                                      (0.1~10)
                                    </>
                                  }
                                  value={'decile'}
                                  component={RadioButton}
                                />
                                <img
                                  src={require('../../../../assets/images/skincare/skincarepro_score_decile.png')}
                                  alt={''}
                                  style={{width: 'inherit'}}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {!smbUtils.isCnSite(auth) && (
                    <CreateAccountForm
                      isCnSite={smbUtils.isCnSite(auth)}
                      width={width}
                    />
                  )}
                </div>
                <div className="d-flex justify-content-end kt-margin-t-20">
                  <ButtonWithRestoreGroup
                    saveDirty={formikProps.dirty}
                    discardButton={false}
                    isUpdating={true}
                    onRestoreClick={() => {
                      handleRestore(formikProps.resetForm);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <PublishToOnlineModal
            isShow={isShowPublishModal}
            onPublish={isPublish => {
              formikProps.setSubmitting(false);
              formikProps.resetForm(formikProps.values);
              setPublishStatus(isPublish);
              setIsShowPublishModal(false);
            }}
          />
          <ProcessingInProgressModal
            show={isShowProcessingModal}
            onHide={() => {
              setIsShowProcessingModal(false);
            }}
          />
        </Form>
      )}
    </Formik>
  );
}
